/* Somnila: apply a discount without leaving the cart or changing its items.
 * Cart API: https://shopify.dev/docs/api/ajax/reference/cart#update-discounts-in-the-cart
 */
(() => {
  'use strict';
  if (window.Sn74CartDiscount) return;

  const selector = 'form[data-sn74-discount]';
  const storageKey = 'somnila.cart.discount.v74';
  const requestTimeout = 12000;
  let pending = false;

  const normalise = value => String(value || '').trim().toLocaleUpperCase();
  const unique = codes => {
    const seen = new Set();
    return codes.filter(code => {
      const key = normalise(code);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };
  const inputOf = form => form.querySelector('input[name="cart-discount-field"]');
  const sameOrigin = value => {
    const url = new URL(value, window.location.href);
    if (url.origin !== window.location.origin) throw new Error('Invalid cart origin');
    return url;
  };
  const cartURL = form => sameOrigin(form.dataset.cartUrl || window.routes?.cart_url || '/cart');
  const apiURL = form => {
    const url = cartURL(form);
    url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
    url.search = '';
    url.hash = '';
    return url;
  };
  const fetchCart = async (url, options = {}, token = null) => {
    if (window.Sn84CartState) {
      const cart = await window.Sn84CartState.request(url.href, options, token);
      if (!cart || !Array.isArray(cart.items)) throw new Error('Cart response missing');
      return cart;
    }
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), requestTimeout);
    try {
      const response = await fetch(url.href, {
        credentials: 'same-origin', cache: 'no-store', ...options,
        headers: { Accept: 'application/json', ...options.headers },
        signal: controller.signal
      });
      if (!response.ok) throw new Error('Cart request failed');
      const cart = await response.json();
      if (!cart || !Array.isArray(cart.items)) throw new Error('Cart response missing');
      return cart;
    } finally { clearTimeout(timeout); }
  };

  const applications = cart => [
    ...(Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : []),
    ...cart.items.flatMap(item => [
      ...(Array.isArray(item.discount_allocations) ? item.discount_allocations : []),
      ...(Array.isArray(item.line_level_discount_allocations) ? item.line_level_discount_allocations : [])
    ].map(allocation => allocation.discount_application).filter(Boolean))
  ].filter(application => application.type === 'discount_code');

  const existingCodes = (cart, form) => {
    if (Array.isArray(cart.discount_codes)) {
      return unique(cart.discount_codes.map(entry => typeof entry === 'string' ? entry : entry?.code).filter(code => typeof code === 'string'));
    }
    const codes = applications(cart).map(application => application.code || application.title).filter(code => typeof code === 'string');
    // Older API responses can omit discount_codes. Liquid supplies only genuine
    // discount_code applications; automatic discount titles are never sent as codes.
    try {
      const liquidCodes = JSON.parse(form.dataset.existingCodes || '[]');
      if (Array.isArray(liquidCodes)) codes.push(...liquidCodes.filter(code => typeof code === 'string'));
    } catch { /* A malformed optional fallback does not replace the API data. */ }
    return unique(codes);
  };
  const classify = (cart, code) => {
    if (Array.isArray(cart.discount_codes)) {
      const found = cart.discount_codes.find(entry => normalise(typeof entry === 'string' ? entry : entry?.code) === normalise(code));
      if (!found) return 'invalid';
      if (found && typeof found === 'object' && typeof found.applicable === 'boolean') return found.applicable ? 'applied' : 'invalid';
    }
    if (applications(cart).some(application => normalise(application.code || application.title) === normalise(code))) return 'applied';
    return 'saved';
  };
  const updateCodes = (form, codes, token) => fetchCart(sameOrigin(form.dataset.updateUrl), {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    // Deliberately no updates, quantity, note, attributes, or line properties.
    body: JSON.stringify({ discount: unique(codes).join(',') })
  }, token);

  const liveForm = (id, inDrawer) => {
    const exact = document.getElementById(id);
    if (exact?.matches(selector)) return exact;
    const scope = inDrawer ? document.querySelector('cart-drawer') : document;
    return scope?.querySelector(selector) || null;
  };
  const show = (form, key, code = '', extra = '', focus = false) => {
    if (!form) return;
    const status = form.querySelector('[data-sn74-discount-status]');
    const input = inputOf(form);
    if (input) {
      if (code) input.value = code;
      if (['empty', 'invalid'].includes(key)) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    }
    if (status) {
      status.textContent = [form.dataset[key], extra].filter(Boolean).join(' ');
      status.hidden = !status.textContent;
      status.dataset.state = key;
    }
    const details = form.closest('details');
    if (details) details.open = true;
    if (focus && input) input.focus();
  };
  const lock = form => {
    const buttons = Array.from(form.querySelectorAll('button[type="submit"], input[type="submit"]'));
    const states = buttons.map(button => button.disabled);
    const input = inputOf(form);
    const wasReadOnly = input?.readOnly;
    buttons.forEach(button => { button.disabled = true; });
    if (input) input.readOnly = true;
    form.setAttribute('aria-busy', 'true');
    return () => {
      buttons.forEach((button, index) => { button.disabled = states[index]; });
      if (input) input.readOnly = wasReadOnly;
      form.removeAttribute('aria-busy');
    };
  };

  const restoreMessage = () => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return;
      const info = JSON.parse(raw);
      if (info.expires < Date.now()) { sessionStorage.removeItem(storageKey); return; }
      const form = liveForm(info.id, info.inDrawer);
      if (!form) return;
      sessionStorage.removeItem(storageKey);
      show(form, info.key, info.code, '', ['empty', 'invalid', 'error'].includes(info.key));
    } catch { /* Storage can be unavailable in private browsing. */ }
  };

  const refreshPageCart = async form => {
    const targets = ['main-cart-items', 'main-cart-footer', 'cart-icon-bubble'];
    const descriptors = targets.map(id => {
      const element = document.getElementById(id);
      const section = element?.dataset.id || element?.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '');
      return { id, element, section };
    }).filter(item => item.element && item.section);
    if (!descriptors.some(item => item.id === 'main-cart-items') || !descriptors.some(item => item.id === 'main-cart-footer')) return false;
    const url = sameOrigin(window.location.href);
    url.searchParams.set('sections', [...new Set(descriptors.map(item => item.section))].join(','));
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), requestTimeout);
    try {
      const response = await fetch(url.href, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Cart sections unavailable');
      const sections = await response.json();
      const parsed = descriptors.map(item => {
        if (typeof sections[item.section] !== 'string') throw new Error('Cart section missing');
        const replacement = new DOMParser().parseFromString(sections[item.section], 'text/html').getElementById(item.id);
        if (!replacement) throw new Error('Cart content missing');
        return { ...item, replacement };
      });
      // Validate every replacement before updating visible prices or line items.
      parsed.forEach(item => {
        if (item.id === 'cart-icon-bubble') item.element.innerHTML = item.replacement.innerHTML;
        else item.element.replaceWith(item.replacement);
      });
      return true;
    } finally { clearTimeout(timeout); }
  };

  const refreshVisible = async (form, info, token) => {
    const drawer = document.querySelector('cart-drawer');
    const onPageCart = Boolean(document.querySelector('cart-items'));
    let refreshed = false;
    if (window.Sn84CartState) {
      try { refreshed = await window.Sn84CartState.refresh(token); } catch { if (token) token.feedback = { error: form.dataset.retry || window.cartStrings?.error || '', retry: true }; }
      if (token?.controller.signal.aborted) return;
      const current = liveForm(info.id, info.inDrawer) || form;
      show(current, info.key, info.code, refreshed ? '' : current.dataset.retry, ['empty', 'invalid', 'error'].includes(info.key));
      return;
    }
    if (onPageCart) {
      try { refreshed = await refreshPageCart(form); } catch { /* Keep the current cart and a retry message. */ }
    }
    if ((info.inDrawer || !onPageCart) && drawer && typeof window.Sn67CartRestore?.refresh === 'function') {
      try {
        await window.Sn67CartRestore.refresh();
        const drawerRefreshed = !drawer.querySelector('[data-sn67-cart-restore-error]');
        refreshed = onPageCart ? refreshed && drawerRefreshed : drawerRefreshed;
      } catch { /* A failed render must not erase the existing cart. */ }
    }
    const current = liveForm(info.id, info.inDrawer) || form;
    show(current, info.key, info.code, refreshed ? '' : current.dataset.retry, ['empty', 'invalid', 'error'].includes(info.key));
  };

  const submit = async event => {
    event.preventDefault();
    const form = event.currentTarget?.matches?.(selector) ? event.currentTarget : event.target.closest(selector);
    if (!form || pending) return false;
    const input = inputOf(form);
    const code = input?.value.trim() || '';
    if (!code) { show(form, 'empty', '', '', true); return false; }
    // The field adds one code. A comma would silently request several codes.
    if (code.includes(',')) { show(form, 'invalid', code, '', true); return false; }
    if (!window.fetch || !window.AbortController || !form.dataset.updateUrl) {
      show(form, 'error', code, form.dataset.retry, true);
      return false;
    }
    const info = { id: form.id, inDrawer: Boolean(form.closest('cart-drawer')), code, key: 'saved' };
    const token = window.Sn84CartState?.begin(form, 'discount') || null;
    if (window.Sn84CartState && !token) return false;
    const unlock = lock(form);
    pending = true;
    let posted = false;
    show(form, 'loading', code);
    try {
      // Read the server cart first, so a failed read never replaces existing codes.
      const before = await fetchCart(apiURL(form), {}, token);
      const previous = existingCodes(before, form);
      posted = true;
      let after = await updateCodes(form, [...previous, code], token);
      info.key = classify(after, code);
      if (info.key === 'invalid') {
        // Restore the exact previous code list when Shopify rejects the new code.
        after = await updateCodes(form, previous, token);
      }
      await refreshVisible(form, info, token);
    } catch {
      info.key = 'error';
      if (posted && !token?.controller.signal.aborted) await refreshVisible(form, info, token);
      else if (!token?.controller.signal.aborted) show(liveForm(info.id, info.inDrawer) || form, 'error', code, form.dataset.retry, true);
    } finally {
      unlock();
      window.Sn84CartState?.end(token);
      pending = false;
    }
    return false;
  };
  const change = event => {
    const form = event.target.closest(selector);
    if (!form || form.hasAttribute('aria-busy')) return;
    inputOf(form)?.removeAttribute('aria-invalid');
    const status = form.querySelector('[data-sn74-discount-status]');
    if (status) { status.hidden = true; status.textContent = ''; delete status.dataset.state; }
  };

  window.Sn74CartDiscount = { submit, change };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', restoreMessage, { once: true });
  else restoreMessage();
})();
